import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getMercados, registrarVisita, getVisitasDelDia } from '../services/firebase';
import Loading from './common/Loading';
import logo from '../assets/ma2.png';
import sinMercadosImg from '../assets/mah.png';
import styles from '../styles/home.module.css';

// ===== ICONOS REACT ICONS =====
import { 
  FaSearch, 
  FaMapMarkerAlt, 
  FaArrowRight, 
  FaLeaf,
  FaTag,
  FaHeart,
  FaStore,
  FaInstagram,
  FaWhatsapp,
  FaChevronLeft,
  FaChevronRight,
  FaTimes
} from 'react-icons/fa';

const Home = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mercados, setMercados] = useState([]);
  const [mercadosFiltrados, setMercadosFiltrados] = useState([]);
  const [error, setError] = useState(null);
  const [fotosCarrusel, setFotosCarrusel] = useState([]);
  const [slideActual, setSlideActual] = useState(0);
  const [visitas, setVisitas] = useState(0);
  
  const [mostrarSelector, setMostrarSelector] = useState(false);
  const [ciudades, setCiudades] = useState([]);
  const [ciudadSeleccionada, setCiudadSeleccionada] = useState('Todas');
  
  const scrollContainerRef = useRef(null);

  // Registrar visita en Firebase y obtener conteo
  useEffect(() => {
    const contarVisita = async () => {
      // Registrar visita en Firebase
      await registrarVisita();
      
      // Obtener visitas del día desde Firebase
      const total = await getVisitasDelDia();
      setVisitas(total);
    };
    
    contarVisita();
  }, []);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError(null);

        const mercadosData = await getMercados();
        if (mercadosData.length === 0) {
          setError('No hay mercados disponibles');
          setLoading(false);
          return;
        }
        setMercados(mercadosData);
        setMercadosFiltrados(mercadosData);

        // Extraer ciudades únicas
        const ciudadesUnicas = ['Todas', ...new Set(
          mercadosData
            .map(m => m.direccion?.ciudad)
            .filter(Boolean)
        )];
        setCiudades(ciudadesUnicas);

        // Construir fotos del carrusel
        const todasLasFotos = [];
        for (const mercado of mercadosData) {
          if (mercado.portada) {
            const ciudad = mercado.direccion?.ciudad || '';
            const estado = mercado.direccion?.estado || '';
            let ubicacion = 'México';
            if (ciudad && estado) {
              ubicacion = `${ciudad}, ${estado}`;
            } else if (ciudad) {
              ubicacion = ciudad;
            }
            
            todasLasFotos.push({
              url: mercado.portada,
              nombre: mercado.nombre,
              id: mercado.id,
              ubicacion: ubicacion
            });
          }
        }
        setFotosCarrusel(todasLasFotos);

      } catch (error) {
        console.error('Error cargando datos:', error);
        setError('Error al cargar los datos. Intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, []);

  // Auto-slide cada 4 segundos
  useEffect(() => {
    if (fotosCarrusel.length === 0) return;

    const interval = setInterval(() => {
      setSlideActual((prev) => (prev + 1) % fotosCarrusel.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [fotosCarrusel.length]);

  const filtrarPorCiudad = (ciudad) => {
    setCiudadSeleccionada(ciudad);
    setMostrarSelector(false);
    
    if (ciudad === 'Todas') {
      setMercadosFiltrados(mercados);
    } else {
      const filtrados = mercados.filter(m => {
        return m.direccion?.ciudad === ciudad;
      });
      setMercadosFiltrados(filtrados);
    }
    
    setTimeout(() => {
      document.getElementById('mercados')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const scrollHorizontal = (direccion) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direccion === 'izquierda' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  const irAMercado = (id) => {
    navigate(`/mercado/${id}`);
  };

  const getFotoMercado = (mercado) => {
    if (mercado.fotosPortada && mercado.fotosPortada.length > 0) {
      const randomIndex = Math.floor(Math.random() * mercado.fotosPortada.length);
      return mercado.fotosPortada[randomIndex];
    }
    return mercado.portada;
  };

  if (loading) {
    return <Loading message="Cargando..." />;
  }

  if (error) {
    return (
      <div className={styles.homeContainer}>
        <header className={styles.homeHeader}>
          <div className={styles.homeHeaderContainer}>
            <Link to="/" className={styles.homeLogo}>
              <img src={logo} alt="Mercado Abierto" className={styles.homeLogoImg} />
            </Link>
          </div>
        </header>
        
        <main className={styles.mainContent}>
          <div className={styles.sinMercadosContainer}>
            <div className={styles.sinMercadosContent}>
              <div className={styles.sinMercadosImageWrapper}>
                <img 
                  src={sinMercadosImg} 
                  alt="Sin mercados disponibles" 
                  className={styles.sinMercadosImage} 
                />
                <div className={styles.sinMercadosPulse}></div>
              </div>
              <h2 className={styles.sinMercadosTitle}>¡Tenemos algo que mostrarte próximamente!</h2>
              <p className={styles.sinMercadosSubtitle}>
                Estamos trabajando para traerte los mejores mercados municipales de México.
              </p>
              <p className={styles.sinMercadosDescription}>
                La información estará disponible muy pronto. ¡Vuelve a visitarnos!
              </p>
             
            </div>
          </div>
        </main>
        
        <footer className={styles.mktFooter}>
          <img src={logo} alt="logo" className={styles.mktFooterLogo} />
          <p>Plataforma digital que conecta a los mercados municipales de México contigo.</p>
          <div className={styles.mktFooterLinks}>
            <p onClick={() => navigate('/')}>Inicio</p>
            <p onClick={() => navigate('/privacidad')}>Aviso de Privacidad</p>
            <p onClick={() => navigate('/terminos')}>Términos y Condiciones</p>
          </div>
          <h4>Síguenos</h4>
          <div className={styles.socials}>
            <FaInstagram size={28} />
            <FaWhatsapp size={28} />
          </div>
          <div className={styles.mktFooterBottom}>
            © {new Date().getFullYear()} mercadoabierto.online - Todos los derechos reservados
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className={styles.homeContainer}>

      {/* HEADER */}
      <header className={styles.homeHeader}>
        <div className={styles.homeHeaderContainer}>
          <Link to="/" className={styles.homeLogo}>
            <img src={logo} alt="Mercado Abierto" className={styles.homeLogoImg} />
          </Link>
        </div>
      </header>

      <main className={styles.mainContent}>

        {/* HERO */}
        <section className={styles.heroSection}>

          <div className={styles.heroContent} style={{ position: 'relative', zIndex: 100 }}>
            <h1 className={styles.heroTitle}>
              Descubre lo mejor
              <br />
              de los mercados
              <br />
              municipales
              <br />
              <span className={styles.heroSubtitle}>24/7</span>
            </h1>

            <p className={styles.heroDescription}>
              Todo en un mismo lugar. Encuentra productos frescos,
              artesanías y más en los mejores mercados de México.
            </p>


            <button
              onClick={() => setMostrarSelector(!mostrarSelector)}
              className={styles.explorarButton}
            >
              <FaSearch size={20} />
              {ciudadSeleccionada === 'Todas' ? 'Explorar mercados' : `Explorar ${ciudadSeleccionada}`}
            </button>

            {/* Selector de ciudades */}
            {mostrarSelector && (
              <div className={styles.selectorCiudades}>
                <div className={styles.selectorHeader}>
                  <span className={styles.selectorTitle}>Selecciona una ciudad</span>
                  <button 
                    className={styles.selectorClose}
                    onClick={() => setMostrarSelector(false)}
                  >
                    <FaTimes size={18} />
                  </button>
                </div>
                <div className={styles.selectorLista}>
                  {ciudades.map((ciudad, index) => (
                    <button
                      key={index}
                      className={`${styles.selectorItem} ${ciudad === ciudadSeleccionada ? styles.selectorItemActive : ''}`}
                      onClick={() => filtrarPorCiudad(ciudad)}
                    >
                      <FaMapMarkerAlt size={16} />
                      {ciudad}
                      {ciudad === ciudadSeleccionada && (
                        <span className={styles.selectorCheck}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CARRUSEL */}
          {fotosCarrusel.length > 0 && (
            <div className={styles.carruselContainer}>
              {fotosCarrusel.map((foto, index) => (
                <div
                  key={index}
                  className={`${styles.carruselSlide} ${index === slideActual ? styles.active : ''}`}
                  style={{
                    transition: 'opacity 1.2s ease-in-out',
                    opacity: index === slideActual ? 1 : 0
                  }}
                >
                  <img
                    src={foto.url}
                    alt={foto.nombre}
                    className={styles.carruselImage}
                  />

                  <div className={styles.carruselCaption}>
                    <h3>{foto.nombre}</h3>
                    <p>{foto.ubicacion}</p>
                  </div>
                </div>
              ))}

              <div className={styles.carruselIndicators}>
                {fotosCarrusel.map((_, index) => (
                  <button
                    key={index}
                    className={`${styles.indicator} ${index === slideActual ? styles.active : ''}`}
                    onClick={() => setSlideActual(index)}
                  />
                ))}
              </div>
            </div>
          )}

        </section>

        {/* MERCADOS */}
        <section id="mercados" className={styles.mercadosSection}>
          <div className={styles.mercadosHeader}>
            <h2 className={styles.mercadosTitle}>
              <FaStore size={28} />
              {ciudadSeleccionada === 'Todas' ? 'Mercados Disponibles' : `Mercados en ${ciudadSeleccionada}`}
            </h2>
            <span className={styles.mercadosCount}>
              {mercadosFiltrados.length} {mercadosFiltrados.length === 1 ? 'mercado' : 'mercados'}
            </span>
          </div>

          {mercadosFiltrados.length > 5 && (
            <div className={styles.scrollButtons}>
              <button 
                className={styles.scrollBtn} 
                onClick={() => scrollHorizontal('izquierda')}
              >
                <FaChevronLeft size={20} />
              </button>
              <button 
                className={styles.scrollBtn} 
                onClick={() => scrollHorizontal('derecha')}
              >
                <FaChevronRight size={20} />
              </button>
            </div>
          )}

          <div 
            className={styles.mercadosScrollContainer} 
            ref={scrollContainerRef}
          >
            <div className={styles.mercadosGrid}>
              {mercadosFiltrados.length > 0 ? (
                mercadosFiltrados.map((mercado) => (
                  <div
                    key={mercado.id}
                    className={styles.mercadoCard}
                    onClick={() => irAMercado(mercado.id)}
                  >
                    <img
                      src={getFotoMercado(mercado)}
                      alt={mercado.nombre}
                      className={styles.mercadoThumb}
                    />

                    <div className={styles.mercadoInfo}>
                      <h3>{mercado.nombre}</h3>

                      {mercado.direccion?.ciudad && (
                        <p className={styles.mercadoUbicacion}>
                          <FaMapMarkerAlt size={14} />
                          {mercado.direccion.ciudad}
                          {mercado.direccion.estado && `, ${mercado.direccion.estado}`}
                        </p>
                      )}

                      <p className={styles.mercadoDescripcion}>
                        {mercado.descripcion
                          ? mercado.descripcion.substring(0, 80) + '...'
                          : 'Descubre productos y comerciantes locales.'}
                      </p>

                      <span className={styles.mercadoLink}>
                        Ver mercado
                        <FaArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noMercados}>
                  <p>No hay mercados disponibles en esta ciudad</p>
                  <button 
                    className={styles.verTodasCiudades}
                    onClick={() => filtrarPorCiudad('Todas')}
                  >
                    Ver todos los mercados
                  </button>
                </div>
              )}
            </div>
          </div>

          {mercadosFiltrados.length > 5 && (
            <div className={styles.scrollIndicators}>
              {Array.from({ length: Math.ceil(mercadosFiltrados.length / 5) }).map((_, index) => (
                <span key={index} className={styles.scrollDot} />
              ))}
            </div>
          )}
        </section>

        {/* BENEFICIOS */}
        <section className={styles.beneficiosSection}>
          <div 
            className={`${styles.beneficioCard} ${styles.beneficioMapa}`}
            onClick={() => navigate('/mapa')}
          >
            <FaMapMarkerAlt size={46} />
            <h3>Encuentra el mercado más cercano</h3>
            <p>Localiza los mercados cerca de ti con nuestro mapa interactivo.</p>
          </div>

          <div className={styles.beneficioCard}>
            <FaTag size={46} />
            <h3>Mejores precios</h3>
            <p>Compra y vuelve a lo local.</p>
          </div>

          <div className={styles.beneficioCard}>
            <FaHeart size={46} />
            <h3>Consumo Local</h3>
            <p>Impulsamos la economía local.</p>
          </div>
        </section>

        {/* FOOTER */}
        <footer className={styles.mktFooter}>
          <img src={logo} alt="logo" className={styles.mktFooterLogo} />
          
          <p>Plataforma digital que conecta a los mercados municipales de México contigo.</p>
          
          <div className={styles.mktFooterLinks}>
            <p onClick={() => navigate('/')}>Inicio</p>
            <p onClick={() => navigate('/')}>Explorar mercados</p>
            <p onClick={() => navigate('/privacidad')}>Aviso de Privacidad</p>
            <p onClick={() => navigate('/terminos')}>Términos y Condiciones</p>
          </div>
          
          <h4>Síguenos</h4>
          <div className={styles.socials}>
            <FaInstagram size={28} />
            <FaWhatsapp size={28} />
          </div>
          
          <div className={styles.mktFooterBottom}>
            © {new Date().getFullYear()}  MercadoAbierto.online - Todos los derechos reservados.
          </div>
        </footer>

      </main>
    </div>
  );
};

export default Home;